import { useEffect, useState, type ReactElement } from 'react';
import { useDispatchJobEvent } from '../api/jobs';
import { Modal } from './Modal';
import { TextAreaField } from './FormField';
import { ActionButton } from './ActionButton';
import { InlineError } from './InlineError';
import { useToast } from './toastContext';
import type { Job, JobEventRequest } from '../types';

type ReviewKind = 'complete' | 'fail';

// True once `iso` is older than `ms`. If it isn't yet, schedule a one-shot
// re-render at the threshold — "now" is captured once at mount (React 19
// purity rule; same idiom as RelativeTime), so without this the button would
// only appear on the next external update.
function useOlderThan(iso: string, ms: number): boolean {
  const [old, setOld] = useState(() => Date.now() - Date.parse(iso) > ms);
  useEffect(() => {
    if (old) return;
    const remaining = ms - (Date.now() - Date.parse(iso));
    // setTimeout (even with a 0ms floor) always fires asynchronously, so this
    // never calls setState synchronously from within the effect body.
    const timer = setTimeout(() => setOld(true), Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [iso, ms, old]);
  return old;
}

// Job-level action row for the JobDrawer header (A1EE-338). Re-run on FAILED or
// REVIEW; Complete/Fail only on REVIEW. Complete reason optional, Fail reason
// required. SSE + invalidate repaint the job state after the transition.
export function JobActions({ job }: { job: Job }): ReactElement | null {
  // Hooks must run unconditionally on every render, so the interactive
  // short-circuit lives below them, not above — PROMPT/ORCA state (dispatch,
  // review modal) is simply unused when job.mode === 'INTERACTIVE'.
  const dispatch = useDispatchJobEvent(job.id);
  const { showToast } = useToast();
  const [reviewKind, setReviewKind] = useState<ReviewKind | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  if (job.mode === 'INTERACTIVE') return <InteractiveJobActions job={job} />;

  const canRerun = job.state === 'FAILED' || job.state === 'REVIEW';
  const inReview = job.state === 'REVIEW';
  if (!canRerun && !inReview) return null;

  const closeModal = () => {
    setReviewKind(null);
    setReason('');
    setReasonError(null);
  };

  const rerun = () => {
    dispatch.mutate(
      { event: 'RetryJob' },
      {
        onSuccess: () => showToast('Re-run dispatched', 'success'),
        onError: () => showToast('Re-run failed', 'error'),
      },
    );
  };

  const confirmReview = () => {
    const trimmed = reason.trim();
    if (reviewKind === 'fail' && trimmed.length === 0) {
      setReasonError('Reason is required to fail a review.');
      return;
    }
    const request =
      reviewKind === 'fail'
        ? ({ event: 'ReviewFailed', reason: trimmed } as const)
        : trimmed.length > 0
          ? ({ event: 'ReviewPassed', reason: trimmed } as const)
          : ({ event: 'ReviewPassed' } as const);
    dispatch.mutate(request, {
      onSuccess: () => {
        showToast(reviewKind === 'fail' ? 'Job failed' : 'Job completed', 'success');
        closeModal();
      },
    });
  };

  return (
    <div className="flex flex-wrap gap-2">
      {canRerun && (
        <ActionButton
          onClick={rerun}
          variant="neutral"
          pending={dispatch.isPending && reviewKind === null}
          pendingLabel="Re-running…"
        >
          Re-run
        </ActionButton>
      )}
      {inReview && (
        <>
          <ActionButton onClick={() => setReviewKind('complete')} variant="primary">
            Complete
          </ActionButton>
          <ActionButton onClick={() => setReviewKind('fail')} variant="danger">
            Fail
          </ActionButton>
        </>
      )}

      <Modal
        open={reviewKind !== null}
        onClose={closeModal}
        title={reviewKind === 'fail' ? 'Fail review' : 'Complete review'}
        footer={
          <>
            <ActionButton onClick={closeModal} variant="neutral">
              Cancel
            </ActionButton>
            <ActionButton
              onClick={confirmReview}
              variant={reviewKind === 'fail' ? 'danger' : 'primary'}
              pending={dispatch.isPending}
              pendingLabel="Submitting…"
            >
              {reviewKind === 'fail' ? 'Confirm fail' : 'Confirm complete'}
            </ActionButton>
          </>
        }
      >
        <div className="space-y-3">
          {dispatch.isError && (
            <InlineError heading="Couldn't update the job" error={dispatch.error} />
          )}
          <TextAreaField
            id="review-reason"
            label={reviewKind === 'fail' ? 'Reason' : 'Reason (optional)'}
            value={reason}
            onChange={setReason}
            error={reasonError}
            required={reviewKind === 'fail'}
            rows={4}
            placeholder={
              reviewKind === 'fail'
                ? 'Why is this review failing?'
                : 'Optional note about the resolution'
            }
          />
        </div>
      </Modal>
    </div>
  );
}

// Interactive-session lifecycle actions (A1EE-575). QUEUED = created but never
// started: Start (+ Discard once >5s old, to avoid a flash during autostart).
// Terminal (COMPLETED/FAILED/CANCELLED) = Restart, reusing the durable volume.
// RUNNING renders nothing — the live run is reachable via the drawer's run list.
function InteractiveJobActions({ job }: { job: Job }): ReactElement | null {
  const dispatch = useDispatchJobEvent(job.id);
  const { showToast } = useToast();
  const [firing, setFiring] = useState<JobEventRequest['event'] | null>(null);
  const canDiscard = useOlderThan(job.createdAt, 5000);

  if (job.state === 'RUNNING') return null;

  const fire = (event: JobEventRequest['event'], okMsg: string, errMsg: string) => {
    setFiring(event);
    dispatch.mutate(
      { event },
      {
        onSuccess: () => showToast(okMsg, 'success'),
        onError: () => showToast(errMsg, 'error'),
        onSettled: () => setFiring(null),
      },
    );
  };

  return (
    <div className="space-y-2">
      {job.state === 'QUEUED' ? (
        <>
          <div className="flex flex-wrap gap-2">
            <ActionButton
              onClick={() => fire('StartJob', 'Session starting', 'Start failed')}
              variant="primary"
              pending={dispatch.isPending && firing === 'StartJob'}
              pendingLabel="Starting…"
            >
              Start
            </ActionButton>
            {canDiscard && (
              <ActionButton
                onClick={() => fire('AbandonInteractiveJob', 'Session discarded', 'Discard failed')}
                variant="danger"
                pending={dispatch.isPending && firing === 'AbandonInteractiveJob'}
                pendingLabel="Discarding…"
              >
                Discard
              </ActionButton>
            )}
          </div>
          <p className="text-xs text-slate-500">Not started yet — no sandbox volume.</p>
        </>
      ) : (
        <ActionButton
          onClick={() => fire('RetryJob', 'Restart dispatched', 'Restart failed')}
          variant="neutral"
          pending={dispatch.isPending && firing === 'RetryJob'}
          pendingLabel="Restarting…"
        >
          Restart
        </ActionButton>
      )}
      {dispatch.isError && (
        <InlineError heading="Couldn't update the session" error={dispatch.error} />
      )}
    </div>
  );
}
